import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface BrandAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  isPrescription?: boolean;
}

interface Brand {
  id: number;
  name: string;
  country: string | null;
  is_prescription: boolean;
}

export const BrandAutocomplete = ({ value, onChange, isPrescription }: BrandAutocompleteProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Brand[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load brands on mount and when search changes
  useEffect(() => {
    loadBrands(value);
  }, [value, isPrescription]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadBrands = async (search: string) => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('pet_food_brands')
        .select('*')
        .order('sort_order', { ascending: true })
        .limit(15);

      if (search) {
        query = query.ilike('name', `%${search}%`);
      }

      // Filter by prescription if needed
      if (isPrescription) {
        query = query.eq('is_prescription', true);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSuggestions(data || []);
    } catch (error) {
      console.error('Error loading brands:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (brand: Brand) => {
    onChange(brand.name);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder="Royal Canin, Hill's, Taste of the Wild..."
      />
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : suggestions.length > 0 ? (
            <ul className="py-1">
              {suggestions.map((brand) => (
                <li
                  key={brand.id}
                  className={cn(
                    "px-3 py-2 cursor-pointer hover:bg-accent transition-colors",
                    value === brand.name && "bg-accent"
                  )}
                  onClick={() => handleSelect(brand)}
                >
                  <div className="font-medium">{brand.name}</div>
                  {brand.country && (
                    <div className="text-xs text-muted-foreground">{brand.country}</div>
                  )}
                </li>
              ))}
            </ul>
          ) : value ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No matching brands. You can use "{value}" as custom brand.
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
